// ---------------------------------------------------------------------------
// Audit Log - data hook
//
// Centralises all data plumbing for the audit log page: query against
// the admin API, a mock-data fallback for local development, client-side
// filtering of mock data to match the applied filters, pagination slicing
// and per-day KPI counts.
// ---------------------------------------------------------------------------

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { auditLogApi } from "@/lib/admin-api";
import type { AuditLogEntry, AuditLogListParams } from "@/lib/admin-api/audit-log";
import { AUTO_REFRESH_INTERVAL, PAGE_SIZE } from "./constants";
import { generateMockEntries } from "./mockEntries";

interface AuditLogDataFilters {
  readonly page: number;
  readonly debouncedSearch: string;
  readonly actionFilter: string;
  readonly resourceFilter: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly userFilter: string;
}

interface AuditLogDataResult {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly usingMock: boolean;
  readonly paginatedEntries: readonly AuditLogEntry[];
  readonly pagination: { page: number; limit: number; total: number };
  readonly totalPages: number;
  readonly kpis: {
    readonly todayActions: number;
    readonly activeUsers: number;
    readonly modifications: number;
    readonly deletions: number;
  };
}

export function useAuditLogData(
  filters: AuditLogDataFilters,
): AuditLogDataResult {
  const {
    page,
    debouncedSearch,
    actionFilter,
    resourceFilter,
    dateFrom,
    dateTo,
    userFilter,
  } = filters;

  const params: AuditLogListParams = {
    page,
    limit: PAGE_SIZE,
    q: debouncedSearch || undefined,
    action: actionFilter || undefined,
    resource: resourceFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    admin_user_id: userFilter || undefined,
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-audit-log-page", params],
    queryFn: () => auditLogApi.list(params),
    staleTime: 15_000,
    refetchInterval: AUTO_REFRESH_INTERVAL,
    placeholderData: (prev) => prev,
  });

  // Use API data if available, fall back to mock data
  const mockEntries = useMemo(() => generateMockEntries(), []);

  const apiEntries = data?.entries ?? [];
  const usingMock = apiEntries.length === 0 && !isLoading && !isError;
  const allEntries = usingMock ? mockEntries : apiEntries;

  // Client-side filtering for mock data
  const filteredEntries = useMemo(() => {
    if (!usingMock) return allEntries;

    return allEntries.filter((entry) => {
      if (actionFilter && entry.action !== actionFilter) return false;
      if (resourceFilter && entry.resource !== resourceFilter) return false;
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        const userName = entry.admin_user?.full_name?.toLowerCase() ?? "";
        const resource = entry.resource.toLowerCase();
        const resourceId = (entry.resource_id ?? "").toLowerCase();
        if (!userName.includes(q) && !resource.includes(q) && !resourceId.includes(q)) {
          return false;
        }
      }
      if (dateFrom && entry.created_at < dateFrom) return false;
      if (dateTo && entry.created_at > dateTo + "T23:59:59") return false;
      if (userFilter) {
        const userName = entry.admin_user?.full_name?.toLowerCase() ?? "";
        if (!userName.includes(userFilter.toLowerCase())) return false;
      }
      return true;
    });
  }, [allEntries, usingMock, actionFilter, resourceFilter, debouncedSearch, dateFrom, dateTo, userFilter]);

  // Pagination for mock data
  const pagination = usingMock
    ? { page, limit: PAGE_SIZE, total: filteredEntries.length }
    : data?.pagination ?? { page: 1, limit: PAGE_SIZE, total: 0 };

  const paginatedEntries = usingMock
    ? filteredEntries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
    : apiEntries;

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  // KPI computations
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayStr = todayStart.toISOString();

  const kpiSource = usingMock ? filteredEntries : allEntries;
  const todayActions = kpiSource.filter((e) => e.created_at >= todayStr).length;
  const activeUsers = new Set(
    kpiSource.filter((e) => e.created_at >= todayStr).map((e) => e.admin_user_id),
  ).size;
  const modifications = kpiSource.filter(
    (e) => e.created_at >= todayStr && (e.action === "update" || e.action === "status_change"),
  ).length;
  const deletions = kpiSource.filter(
    (e) => e.created_at >= todayStr && e.action === "delete",
  ).length;

  return {
    isLoading,
    isError,
    usingMock,
    paginatedEntries,
    pagination,
    totalPages,
    kpis: { todayActions, activeUsers, modifications, deletions },
  };
}
